// src/pages/therapist/TherapistAllAppointments.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../context/AuthProvider";
import API_BASE_URL from "../../config/apiConfig";
import TherapistSidebar from "../../components/TherapistSidebar";
import ExportSchedule from "../../components/ExportSchedule";
import avatar from "../../assets/images/icon-avatar.svg";

/* ═══════ Types ═══════ */
interface TimeInterval {
  start: string;
  end: string;
}

interface Appointment {
  id: string;
  therapist_id: string;
  patient_name: string;
  type: string;
  appointment_description: string;
  status?: string;
  start_time: string;
  end_time: string;
}

interface Therapist {
  user_id: string;
  firstname: string;
  lastname: string;
  is_active: boolean;
  therapist_category?: string | null;
  order_in_category?: number | null;
}

interface TherapistCategory {
  key: string;
  value: string;
  priority: string;
  color: string;
}

interface LabeledItem {
  key: string;
  value: string;
  short_name?: string;
}

interface StatusItem {
  key: string;
  value: string;
  color: string;
  short_name?: string;
}

/* ═══════ Helpers ═══════ */
const formatDateToYMD = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const isSameISO = (iso: string, date: string, hhmm: string) =>
  iso === `${date}T${hhmm}:00`;

const hexToRGBA = (hex: string, opacity: number): string => {
  hex = hex.replace("#", "");
  if (hex.length !== 6) return `rgba(200,200,200,${opacity})`;
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/* ═══════ Component ═══════ */
const TherapistAllAppointments: React.FC = () => {
  const { token } = useAuth();

  const [isCollapsed, setIsCollapsed] = useState<boolean>(
    () => localStorage.getItem("sidebarCollapsedTherapist") === "true"
  );
  const [viewMode, setViewMode] = useState<"scroll" | "full">("scroll");
  const [selectedDate, setSelectedDate] = useState(formatDateToYMD(new Date()));

  const [therapists, setTherapists] = useState<Therapist[]>([]);
  const [categories, setCategories] = useState<TherapistCategory[]>([]);
  const [timeIntervals, setTimeIntervals] = useState<TimeInterval[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);

  const [therapyTypes, setTherapyTypes] = useState<LabeledItem[]>([]);
  const [activities, setActivities] = useState<LabeledItem[]>([]);
  const [statuses, setStatuses] = useState<StatusItem[]>([]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  /* ─── Fetch terapeuți ─── */
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/users/therapists`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await r.json();
        setTherapists(Array.isArray(data) ? data : data?.data || []);
      } catch (e) {
        console.error("Eroare terapeuți:", e);
      }
    })();
  }, [token]);

  /* ─── Fetch categorii ─── */
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/types/therapist-categories`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await r.json();
        setCategories(data.data || []);
      } catch (e) {
        console.error("Eroare categorii:", e);
      }
    })();
  }, [token]);

  /* ─── Fetch intervale orare ─── */
  useEffect(() => {
    if (!token || !selectedDate) return;
    (async () => {
      try {
        const r = await fetch(
          `${API_BASE_URL}/types/time-intervals?date=${selectedDate}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await r.json();
        setTimeIntervals(data.data || []);
      } catch (e) {
        console.error("Eroare intervale:", e);
      }
    })();
  }, [token, selectedDate]);

  /* ─── Fetch programări ─── */
  useEffect(() => {
    if (!token || !selectedDate) return;
    const controller = new AbortController();
    (async () => {
      try {
        const r = await fetch(
          `${API_BASE_URL}/appointments/get-appointments?date=${selectedDate}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          }
        );
        const data = await r.json();
        setAppointments(data.data || []);
      } catch (e) {
        if ((e as any)?.name === "AbortError") return;
        console.error("Eroare programări:", e);
      }
    })();
    return () => controller.abort();
  }, [token, selectedDate]);

  /* ─── Fetch dropdowns (terapii, activități, statusuri) ─── */
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const [thRes, acRes, stRes] = await Promise.all([
          fetch(`${API_BASE_URL}/types/therapy-type`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_BASE_URL}/types/activities`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_BASE_URL}/types/appointment-status`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        const [thData, acData, stData] = await Promise.all([
          thRes.json(),
          acRes.json(),
          stRes.json(),
        ]);
        setTherapyTypes(thData.data || []);
        setActivities(acData.data || []);
        setStatuses(stData.data || []);
      } catch (e) {
        console.error("Eroare dropdowns:", e);
      }
    })();
  }, [token]);

  /* ─── Label helpers ─── */
  const getTherapyValue = (key: string) =>
    therapyTypes.find((t) => t.key === key)?.value || key;
  const getTherapyShort = (key: string) =>
    therapyTypes.find((t) => t.key === key)?.short_name || key;
  const getActivityValue = (key: string) =>
    activities.find((a) => a.key === key)?.value || key;
  const getActivityShort = (key: string) =>
    activities.find((a) => a.key === key)?.short_name || key;

  const getStatusLabel = (key: string) => {
    const st = statuses.find((s) => s.key === key);
    return st ? st : null;
  };

  /* ─── Sortare terapeuți (doar activi, fără medici) ─── */
  const sortedTherapists = useMemo(() => {
    const active = therapists.filter((t) => t.is_active);

    const getPriority = (t: Therapist) => {
      const cat = categories.find((c) => c.key === t.therapist_category);
      return cat ? Number(cat.priority) : 999;
    };

    return [...active].sort((a, b) => {
      const pA = getPriority(a);
      const pB = getPriority(b);
      if (pA !== pB) return pA - pB;
      const oA = a.order_in_category ?? 9999;
      const oB = b.order_in_category ?? 9999;
      if (oA !== oB) return oA - oB;
      return `${a.lastname} ${a.firstname}`
        .toLowerCase()
        .localeCompare(`${b.lastname} ${b.firstname}`.toLowerCase());
    });
  }, [therapists, categories]);

  /* ─── Render ─── */
  return (
    <div className={`dashboard-container ${isCollapsed ? "collapsed" : ""}`}>
      <TherapistSidebar
        onCollapseChange={(collapsed) => setIsCollapsed(collapsed)}
      />
      <div className="page-content">
        <h1 className="page-title">Toate programările</h1>

        <div className="table-top">
          <div className="page-subtitle">Vizualizare programări</div>
          <div className="appointments-actions">

            <div className="appointment-date">
              <label>Data:</label>
              <input
                type="date"
                min="2025-01-01"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="mode-buttons">
          <button
            className={viewMode === "scroll" ? "mod-active" : ""}
            onClick={() => setViewMode("scroll")}
          >
            Mod scroll
          </button>
          <button
            className={viewMode === "full" ? "mod-active" : ""}
            onClick={() => setViewMode("full")}
          >
            Mod complet
          </button>
        </div>

        <div
          className={
            viewMode === "scroll"
              ? "scrollable-container"
              : "full-width-container"
          }
          ref={scrollContainerRef}
        >
          <div className="appointments-table">
            {/* ── Header ── */}
            <div className="table-header">
              <div className="therapist-header">Terapeut</div>
              <div className="appointments-header-scroll">
                {timeIntervals.map((iv) => (
                  <div key={iv.start} className="appointment-header-cell">
                    {iv.start} - {iv.end}
                  </div>
                ))}
              </div>
            </div>

            {/* ── Body ── */}
            <div className="table-body">
              {sortedTherapists.map((therapist) => {
                const cat = categories.find(
                  (c) => c.key === therapist.therapist_category
                );
                const bgColor = cat
                  ? hexToRGBA(cat.color, 1)
                  : "transparent";

                return (
                  <div key={therapist.user_id} className="appointment-row">
                    <div
                      className="therapist-cell"
                      style={{ backgroundColor: bgColor }}
                    >
                      <img src={avatar} alt="user" className="user-icon" />
                      {therapist.firstname} {therapist.lastname}
                    </div>
                    <div className="appointments-row-scroll">
                      {timeIntervals.map((interval) => {
                        const cellApps = appointments.filter(
                          (a) =>
                            a.therapist_id === therapist.user_id &&
                            isSameISO(
                              a.start_time,
                              selectedDate,
                              interval.start
                            )
                        );

                        return (
                          <div
                            key={`${therapist.user_id}-${interval.start}`}
                            className="appointment-cell cell-readonly"
                          >
                            <div className="appointment-cell-content">
                              {cellApps.map((a) => {
                                const isTherapy = a.type === "therapy";
                                const statusObj = getStatusLabel(
                                  a.status || ""
                                );

                                return (
                                  <div
                                    key={a.id}
                                    className="appointment-block readonly-block"
                                  >
                                    {/* Pacient */}
                                    <div className="appointment-value appointment-patient">
                                      <span className="appointment-text">
                                        Pacient:
                                      </span>{" "}
                                      {a.patient_name || "—"}
                                    </div>

                                    {/* Tip */}
                                    {isTherapy ? (
                                      <div className="appointment-value appointment-type">
                                        <span className="appointment-text">
                                          Terapie:
                                        </span>{" "}
                                        {viewMode === "scroll"
                                          ? getTherapyValue(
                                              a.appointment_description
                                            )
                                          : getTherapyShort(
                                              a.appointment_description
                                            )}
                                      </div>
                                    ) : (
                                      <div className="appointment-value appointment-type">
                                        <span className="appointment-text">
                                          Activitate:
                                        </span>{" "}
                                        {viewMode === "scroll"
                                          ? getActivityValue(
                                              a.appointment_description
                                            )
                                          : getActivityShort(
                                              a.appointment_description
                                            )}
                                      </div>
                                    )}

                                    {/* Status (read-only) */}
                                    <div className="appointment-value appointment-status">
                                      <span className="appointment-text">
                                        Status:
                                      </span>
                                      {statusObj ? (
                                        <span
                                          className="status-readonly-badge"
                                          style={{
                                            backgroundColor: hexToRGBA(
                                              statusObj.color,
                                              0.15
                                            ),
                                            color: statusObj.color,
                                          }}
                                        >
                                          {viewMode === "scroll"
                                            ? statusObj.value
                                            : statusObj.short_name ||
                                              statusObj.value}
                                        </span>
                                      ) : (
                                        <span className="status-readonly">
                                          {a.status || "—"}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TherapistAllAppointments;